import React, { useState, useEffect } from 'react';
import { freelancersAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { UserCheck, Plus, Edit2, Trash2, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminFreelancers() {
  const [freelancers, setFreelancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', specialization: '', rate: '', rate_type: 'per_article', availability: 'available', notes: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try { const res = await freelancersAPI.list(); setFreelancers(res.data.freelancers || []); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      if (editing) { await freelancersAPI.update(editing.id, form); toast.success('Updated'); }
      else { await freelancersAPI.create(form); toast.success('Added'); }
      setShowDialog(false); setEditing(null);
      setForm({ name: '', email: '', phone: '', specialization: '', rate: '', rate_type: 'per_article', availability: 'available', notes: '' });
      loadData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await freelancersAPI.delete(id); toast.success('Removed'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const AVAIL_COLORS = { available: '#1F7A5A', busy: '#9A6B1A', unavailable: '#B3261E' };

  return (
    <div className="space-y-6" data-testid="admin-freelancers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><UserCheck className="w-6 h-6 text-[#B89B5E]" /> Freelancer Network</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage external contributors and freelancers</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: '', email: '', phone: '', specialization: '', rate: '', rate_type: 'per_article', availability: 'available', notes: '' }); setShowDialog(true); }} data-testid="add-freelancer-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> Add Freelancer</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
      ) : freelancers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {freelancers.map(f => (
            <Card key={f.id} className="bg-white border-[#E7E0D6]" data-testid="freelancer-card">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{f.name}</h3>
                    <Badge className="text-[10px] mt-1" style={{ background: `${AVAIL_COLORS[f.availability] || '#5B616E'}15`, color: AVAIL_COLORS[f.availability] || '#5B616E' }}>{f.availability}</Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditing(f); setForm({ name: f.name, email: f.email || '', phone: f.phone || '', specialization: f.specialization || '', rate: f.rate || '', rate_type: f.rate_type || 'per_article', availability: f.availability || 'available', notes: f.notes || '' }); setShowDialog(true); }}><Edit2 className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#B3261E]" onClick={() => handleDelete(f.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                {f.specialization && <p className="text-xs text-[#5B616E] mt-2">{f.specialization}</p>}
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E7E0D6] text-xs text-[#5B616E]">
                  {f.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {f.email}</div>}
                  {f.rate && <span>{f.rate_type === 'per_article' ? `$${f.rate}/article` : `$${f.rate}/hr`}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-[#5B616E]"><UserCheck className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>No freelancers</p></div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit Freelancer' : 'Add Freelancer'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Name</label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="freelancer-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Email</label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="text-sm font-medium mb-1 block">Phone</label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Specialization</label><Input value={form.specialization} onChange={e => setForm({ ...form, specialization: e.target.value })} placeholder="e.g. Sports, Politics, Tech" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Rate</label><Input value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} placeholder="Amount" /></div>
              <div><label className="text-sm font-medium mb-1 block">Availability</label>
                <Select value={form.availability} onValueChange={v => setForm({ ...form, availability: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                    <SelectItem value="unavailable">Unavailable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Notes</label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#121316] text-white hover:bg-[#1B1D22]">{editing ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
